import { fetchProductDiscountDescription } from '@api/graphqlContentFul'
import logBuildError from '@helpers/misc/logBuildError'
import { transformProductOptions } from '@helpers/products/transformProductOptions'
import { getValueFromObject } from '@helpers/utils'
import { EmptyObject } from 'react-hook-form'
import { ProductDiscountItem } from 'types/contentful/graphQlApi'
import { Product, ProductByType } from 'types/product'

const handleProductsBuildError = logBuildError('products')

export type DiscountByProductType = Record<
  string,
  { title: string; description: string } | EmptyObject
>

type ProductWithDiscountTags = {
  product_type: string
  tags: string | string[]
}

type ProductTypeWithDiscountTags = {
  product_type: string
  products: ProductWithDiscountTags[]
}

export function productHasDiscountTag(tags: string | string[] = '') {
  const productTags = Array.isArray(tags)
    ? tags
    : tags.split(',').map(tag => tag.trim())

  return productTags.includes('multibuy')
}

export function getDiscountProductTypes(
  productTypesData: ProductTypeWithDiscountTags[]
) {
  return Array.from(
    new Set(
      productTypesData.flatMap(productTypeData =>
        (productTypeData.products || [])
          .filter(product => productHasDiscountTag(product.tags))
          .map(product => product.product_type || productTypeData.product_type)
      )
    )
  )
}

export async function fetchProductDiscount(
  productTypes: string[] = [],
  preview = false
) {
  const discountByProductType: DiscountByProductType = {}

  if (!productTypes.length) {
    return discountByProductType
  }

  const productDiscountResponse = await fetchProductDiscountDescription({
    preview
  })

  const allDiscounts = getValueFromObject<ProductDiscountItem[]>(
    productDiscountResponse,
    'productDiscount.items',
    []
  )
  const discountsWithProductTypes = allDiscounts.filter(
    discount =>
      discount.visibility !== false &&
      Array.isArray(discount.productTypes) &&
      discount.productTypes.length
  )

  productTypes.forEach(productType => {
    const discount = discountsWithProductTypes.find(entry =>
      entry.productTypes?.includes(productType)
    )

    discountByProductType[productType] = discount
      ? {
          title: discount.discountTitle,
          description: discount.discountDescription
        }
      : {}
  })

  return discountByProductType
}

type TransformedProduct = Omit<Product, 'collection'> & {
  byline?: string
}

type TransformedProductByType<TProduct = Product> = {
  color_categories: string[]
  product_type: string
  tab_title: string
  products: TProduct[]
}

type ProductsData = TransformedProductByType<TransformedProduct>
export function createProductsData(
  productTypesData: ProductByType[],
  discountByProductType: DiscountByProductType
): ProductsData[] {
  const transformed: (ProductsData | null)[] = productTypesData.map(
    productTypeData => {
      if (!productTypeData.products?.length) {
        handleProductsBuildError({
          errorLevel: 'warning',
          properties: {
            message: `Missing CAS products - ${productTypeData.product_type}`
          }
        })
        return null
      }

      const updatedProducts = productTypeData.products.map(product => {
        const productTags = Array.isArray(product.tags)
          ? product.tags?.join()
          : product.tags
        const hasDiscount = productHasDiscountTag(product.tags)
        const discount = hasDiscount
          ? discountByProductType[product.product_type] || {}
          : {}

        return {
          id: Number(product.id),
          title: product.title,
          display_name: product.display_name,
          body_html: product.body_html,
          product_type: product.product_type,
          byline: product?.collection?.byline || '',
          handle: product.handle,
          price: product.price,
          compare_at_price: product.compare_at_price,
          options: transformProductOptions(product.options),
          images: product.images,
          image: product.image,
          product_feed: product.product_feed,
          sku16: product.sku16,
          meta: product.meta,
          color: product.color || null,
          tags: productTags,
          discount
        }
      })

      return {
        ...productTypeData,
        products: updatedProducts
      }
    }
  )

  return transformed.filter((item): item is ProductsData => item !== null)
}

type TabbedProductsListResult = {
  productsList: ProductsData[]
  allProducts: TransformedProduct[]
}

export function getProductsListWithTabs(
  productsData: ProductsData[],
  tabsOrder?: string[]
): TabbedProductsListResult {
  const allProducts: TransformedProduct[] = productsData
    .flatMap(data => data.products ?? [])
    .filter(Boolean)

  const tabsWithProductsData: ProductByType[] = tabsOrder
    ? (tabsOrder
        .map(tabTitle =>
          productsData.find(productType => productType?.tab_title === tabTitle)
        )
        .filter(Boolean) as ProductByType[])
    : []

  const productsList =
    tabsWithProductsData.length === productsData.length
      ? tabsWithProductsData
      : productsData

  return {
    productsList,
    allProducts
  }
}
